import { supabase } from './supabase.js';
import { dom } from './dom.js';
import * as state from './state.js';
import { showMessage, setupClickOutside } from './utils.js';
import { loadTransactions } from './transactions.js';
import { updateMonthDisplay } from './calendar.js';
import { t } from './i18n.js';
import { syncService } from './sync.js';

let removeAuthModalListener = () => {};
let resendInterval = null;
const RESEND_COOLDOWN = 60; // seconds

function closeAuthModal() {
    dom.authModal.style.display = 'none';
    removeAuthModalListener();
    stopResendTimer();
    resetAuthForm();
}

function resetAuthForm() {
    // 重置為 Step 1
    dom.authStepEmail.classList.remove('hidden');
    dom.authStepOtp.classList.add('hidden');
    dom.authEmail.value = '';
    dom.authOtp.value = '';
    dom.authMessageEmail.textContent = '';
    dom.authMessageOtp.textContent = '';
    dom.authModalTitle.textContent = t('loginOrRegister');
    stopResendTimer();
}

// 啟動倒數計時器
function startResendTimer() {
    stopResendTimer(); // 清除舊的
    let timeLeft = RESEND_COOLDOWN;
    
    dom.resendOtpBtn.disabled = true;
    dom.resendOtpBtn.classList.add('opacity-50', 'cursor-not-allowed');
    dom.resendTimer.textContent = `(${timeLeft}s)`;

    resendInterval = setInterval(() => {
        timeLeft--;
        if (timeLeft <= 0) {
            stopResendTimer();
            dom.resendOtpBtn.disabled = false;
            dom.resendOtpBtn.classList.remove('opacity-50', 'cursor-not-allowed');
            dom.resendTimer.textContent = ''; // 清空倒數文字
        } else {
            dom.resendTimer.textContent = `(${timeLeft}s)`;
        }
    }, 1000);
}

function stopResendTimer() {
    if (resendInterval) {
        clearInterval(resendInterval);
        resendInterval = null;
    }
    if (dom.resendTimer) dom.resendTimer.textContent = '';
}


export function switchView(viewName) {
    // 1. 先隱藏所有可能的視圖 (包含主視圖與二級視圖)
    dom.ledgerView.style.display = 'none';
    dom.userSettingsView.style.display = 'none';
    if (dom.languageSettingsView) dom.languageSettingsView.style.display = 'none';
    if (dom.fontSettingsView) dom.fontSettingsView.style.display = 'none';
    if (dom.privacyView) dom.privacyView.style.display = 'none';
    if (dom.reportsView) dom.reportsView.style.display = 'none';

    // 2. 根據目標顯示對應視圖
    if (viewName === 'ledger') {
        dom.ledgerView.style.display = 'flex';
        dom.navLedgerBtn.classList.add('active');
        dom.navSettingsBtn.classList.remove('active');

        // --- 進入帳本時的自動重置邏輯 ---
        // 1. 確保捲動回頂部
        if (dom.transactionList) {
            dom.transactionList.scrollTop = 0;
        }

        // 2. 確保回到當月
        const today = new Date();
        if (state.currentDate.getFullYear() !== today.getFullYear() || 
            state.currentDate.getMonth() !== today.getMonth()) {
            state.setCurrentDate(today);
            updateMonthDisplay();
            loadTransactions();
        }
    } else if (viewName === 'settings') {
        dom.userSettingsView.style.display = 'flex';
        dom.navSettingsBtn.classList.add('active');
        dom.navLedgerBtn.classList.remove('active');
    } else if (viewName === 'language') {
        dom.languageSettingsView.style.display = 'flex';
        dom.navSettingsBtn.classList.add('active');
        dom.navLedgerBtn.classList.remove('active');
    }
}

export function updateUserIconStatus(isLoggedIn) {
    // 檢查元素是否存在
    const fillShape = document.getElementById('user-fill-shape');
    const headStroke = document.getElementById('user-head-stroke');
    const bodyStroke = document.getElementById('user-body-stroke');
    
    if (!fillShape || !headStroke || !bodyStroke) return;

    fillShape.classList.toggle('hidden', !isLoggedIn);
    headStroke.classList.toggle('hidden', isLoggedIn);
    bodyStroke.classList.toggle('hidden', isLoggedIn);
}

function updateAuthUI(user) {
    const isLoggedIn = !!user;
    updateUserIconStatus(isLoggedIn);

    // V1: Always hide auth-related UI elements regardless of login status
    if (dom.settingsUserInfo) dom.settingsUserInfo.style.setProperty('display', 'none', 'important');
    if (dom.settingsLoginBtn) dom.settingsLoginBtn.style.setProperty('display', 'none', 'important');
    if (dom.settingsLogoutSection) dom.settingsLogoutSection.style.setProperty('display', 'none', 'important');

    if (isLoggedIn && dom.settingsUserEmail) {
        dom.settingsUserEmail.textContent = user.email;
    }
}

async function handleSignOut(e) {
    e.preventDefault(); 
    const { error } = await supabase.auth.signOut();
    if (error) showMessage(t('logoutFailed', { error: error.message }), 'error');
}

function openAuthModal(e) {
    if (e) e.preventDefault();
    resetAuthForm();
    dom.authModal.style.display = 'flex';
    removeAuthModalListener = setupClickOutside(dom.authModal, dom.authModalContent, closeAuthModal);
}

// 切換到 Step 2 (共用邏輯)
function switchToStep2(email) {
    dom.authStepEmail.classList.add('hidden');
    dom.authStepOtp.classList.remove('hidden');
    dom.otpSentEmail.textContent = email;
    dom.authModalTitle.textContent = t('enterVerificationCode');
    dom.authOtp.focus();
}

// 獲取驗證碼 (Step 1 or Resend)
async function handleGetOtp(isResend = false) {
    return; // V1: Auth disabled
    const email = dom.authEmail.value.trim();
    // 根據是否為重發來決定顯示錯誤訊息的位置
    const messageEl = isResend ? dom.authMessageOtp : dom.authMessageEmail;
    messageEl.textContent = '';
    
    if (!email) {
        // 如果是 Step 1，提示輸入 Email
        if (!isResend) dom.authMessageEmail.textContent = t('emailRequired');
        return;
    }

    try {
        const btn = isResend ? dom.resendOtpBtn : dom.getOtpBtn;
        const originalText = btn.textContent; // 對於 resend 按鈕這可能包含 timer 文字，但無妨，我們會重置
        
        btn.disabled = true;
        // 如果是重發，通常顯示 'Sending...' 或類似
        
        const { error } = await supabase.auth.signInWithOtp({ email });
        if (error) throw error;

        // 如果是 Step 1，切換介面
        if (!isResend) {
            switchToStep2(email);
        } else {
             // 重發成功提示
             showMessage(t('otpResent'));
        }
        
        // 啟動倒數
        startResendTimer();

    } catch (error) {
        console.error("OTP Error:", error);
        messageEl.textContent = t('otpSendFailed', { error: error.message });
        // 如果失敗，確保按鈕恢復
        if (isResend) {
            dom.resendOtpBtn.disabled = false;
        } else {
            dom.getOtpBtn.disabled = false;
        }
    } finally {
        if (!isResend) {
            dom.getOtpBtn.disabled = false;
        }
    }
}

// 「我已有驗證碼」點擊處理
function handleIHaveCode() {
    return; // V1: Auth disabled
    const email = dom.authEmail.value.trim();
    dom.authMessageEmail.textContent = '';
    
    if (!email) {
        dom.authMessageEmail.textContent = t('emailRequired');
        dom.authEmail.focus();
        return;
    }
    
    // 直接跳轉，不發送 OTP
    switchToStep2(email);
    // 這裡不啟動計時器，或者可以啟動一個預設的冷卻（視需求而定），目前不啟動
    // 但要確保重發按鈕是可用的 (除非我們想強制他們等一下？)
    // 邏輯上：如果是「我有驗證碼」，代表可能是之前發的。
    // 如果現在按重發，應該是可以的。
    dom.resendOtpBtn.disabled = false; 
    dom.resendTimer.textContent = '';
}

// 驗證並登入 (Step 2)
async function handleVerifyOtp() {
    return; // V1: Auth disabled
    const email = dom.authEmail.value.trim();
    const token = dom.authOtp.value.trim();
    dom.authMessageOtp.textContent = '';

    if (!token || token.length !== 6) {
        dom.authMessageOtp.textContent = t('invalidVerificationCode');
        return;
    }

    try {
        dom.verifyOtpBtn.disabled = true;
        dom.verifyOtpBtn.textContent = '...';

        const { data, error } = await supabase.auth.verifyOtp({
            email,
            token,
            type: 'email'
        });

        if (error) throw error;

        // 登入成功
        closeAuthModal();
        showMessage(t('loginSuccess'));

    } catch (error) {
        console.error("Verify Error:", error);
        dom.authMessageOtp.textContent = t('loginFailed', { error: error.message });
    } finally {
        dom.verifyOtpBtn.disabled = false;
        dom.verifyOtpBtn.textContent = t('verifyAndLogin');
    }
}

function bindAuthModalEvents() {
    dom.closeAuthModalBtn.addEventListener('click', (e) => { e.preventDefault(); closeAuthModal(); });
    
    dom.getOtpBtn.addEventListener('click', () => handleGetOtp(false));
    dom.iHaveCodeBtn.addEventListener('click', handleIHaveCode);
    dom.verifyOtpBtn.addEventListener('click', handleVerifyOtp);
    dom.resendOtpBtn.addEventListener('click', () => handleGetOtp(true));
    
    dom.backToEmailBtn.addEventListener('click', () => {
        dom.authStepOtp.classList.add('hidden');
        dom.authStepEmail.classList.remove('hidden');
        dom.authModalTitle.textContent = t('loginOrRegister');
        stopResendTimer();
    });

    dom.navSettingsBtn.addEventListener('click', (e) => { 
        e.preventDefault(); 
        switchView('settings');
    });

    dom.navLedgerBtn.addEventListener('click', (e) => {
        e.preventDefault();
        
        // 如果目前已經在帳本視圖，則執行「重置」動作
        if (dom.ledgerView.style.display !== 'none') {
            // 1. 捲動回頂部 (平滑捲動)
            if (dom.transactionList) {
                dom.transactionList.scrollTo({ top: 0, behavior: 'smooth' });
            }

            // 2. 檢查是否需要跳回當月
            const today = new Date();
            const currentViewDate = state.currentDate;
            if (currentViewDate.getFullYear() !== today.getFullYear() || 
                currentViewDate.getMonth() !== today.getMonth()) {
                
                state.setCurrentDate(today); // 重置為今天
                updateMonthDisplay();
                loadTransactions();
            }
        } else {
            // 否則只是切換視圖
            switchView('ledger');
        }
    });

    dom.settingsLoginBtn.addEventListener('click', (e) => { e.preventDefault(); openAuthModal(); });
    dom.logoutBtnModal.addEventListener('click', handleSignOut);
}

export async function initializeAuth() {
    bindAuthModalEvents();
    
    // V1: 統一初始化狀態為本地模式 (userId = null)
    state.setUserId(null); 
    updateAuthUI(null);
    updateMonthDisplay();

    supabase.auth.onAuthStateChange(async (event, session) => {
        console.log(`Auth event: ${event}`);
        const user = session?.user || null;
        
        // V1: 暫時屏蔽登入後的同步行為，確保數據僅存於本地
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
            // state.setUserId(user?.id || null); // V1 保持 null
            // updateAuthUI(user);
            // await loadTransactions();
        }
    });
    
    // 1. 統一加載數據 (V1 恆為訪客/本地模式)
    // 確保只在這裡加載一次，且 userId 確定為 null
    await loadTransactions();
}